"use client";

import React, { useRef } from "react";
import {
  Box,
  Flex,
  IconButton,
  useBreakpointValue,
  Select,
  Text,
} from "@chakra-ui/react";

import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";

interface Category {
  _id: string;
  name: {
    en: string;
    mr: string;
  };
  icon: string;
}

interface CategoryCircleProps {
  name: string;
  icon: string;
  onClick: () => void;
  isSelected: boolean;
}

interface CategoryBarProps {
  showCategories?: boolean;
  category?: Category[];
  selectedCategoryId?: string;
  handleCategoryClick: (categoryId: string) => void;
}

declare global {
  namespace JSX {
    interface IntrinsicElements {
      "ion-icon": React.HTMLAttributes<HTMLElement> & {
        name?: string;
        size?: string;
        color?: string;
        ios?: string;
        md?: string;
      };
    }
  }
}

const CategoryCircle = ({
  name,
  icon,
  onClick,
  isSelected,
}: CategoryCircleProps) => {
  return (
    <Box
      onClick={onClick}
      cursor="pointer"
      px={4}
      py={2}
      borderRadius="full"
      bg={isSelected ? "blue.500" : "gray.100"}
      color={isSelected ? "white" : "gray.700"}
      textAlign="center"
      whiteSpace="nowrap"
      userSelect="none"
      transition="all 0.2s ease"
      display="flex"
      alignItems="center"
      justifyContent="center"
      gap={2}
      border="1px solid"
      borderColor={isSelected ? "blue.500" : "gray.200"}
      _hover={{
        bg: isSelected ? "blue.600" : "gray.200",
      }}
    >
      {React.createElement("ion-icon", {
        name: icon,
        "aria-hidden": "true",
        style: {
          fontSize: "20px",
          width: "24px",
          height: "24px",
          display: "inline-block",
          flexShrink: 0,
        },
      })}

      <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
        {name}
      </Text>
    </Box>
  );
};

const CategoryBar = ({
  showCategories = true,
  category = [],
  selectedCategoryId,
  handleCategoryClick,
}: CategoryBarProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const isMobile = useBreakpointValue({
    base: true,
    md: false,
  });

  const scrollAmount = useBreakpointValue({
    base: 150,
    md: 300,
  });

  const scrollLeft = () => {
    if (!scrollRef.current) return;

    scrollRef.current.scrollBy({
      left: -(scrollAmount || 300),
      behavior: "smooth",
    });
  };

  const scrollRight = () => {
    if (!scrollRef.current) return;

    scrollRef.current.scrollBy({
      left: scrollAmount || 300,
      behavior: "smooth",
    });
  };

  if (!showCategories) {
    return null;
  }

  return (
    <Box
      px={{
        base: 2,
        sm: 4,
        md: 6,
      }}
      py={2}
      width="100%"
      maxW="container.xl"
      mx="auto"
    >
      {isMobile ? (
        <Select
          placeholder="Select Category"
          onChange={(e) => {
            const categoryId = e.target.value;

            if (categoryId) {
              handleCategoryClick(categoryId);
            }
          }}
          value={selectedCategoryId || ""}
          size="md"
          bg="white"
        >
          {category.map((c) => (
            <option key={c._id} value={c._id}>
              {c.name.en}
            </option>
          ))}
        </Select>
      ) : (
        <Flex align="center" gap={2} position="relative">
          <IconButton
            aria-label="Scroll Left"
            icon={<ArrowBackIcon />}
            onClick={scrollLeft}
            variant="ghost"
            size="sm"
          />
          <Box
            ref={scrollRef}
            overflowX="auto"
            whiteSpace="nowrap"
            flex="1"
            display="flex"
            sx={{
              scrollbarWidth: "none",

              "&::-webkit-scrollbar": {
                display: "none",
              },

              scrollSnapType: "x mandatory",

              WebkitOverflowScrolling: "touch",
            }}
          >
            <Box display="inline-flex" gap={3}>
              {category.map((c) => (
                <CategoryCircle
                  key={c._id}
                  name={c.name.en}
                  icon={c.icon}
                  onClick={() => handleCategoryClick(c._id)}
                  isSelected={selectedCategoryId === c._id}
                />
              ))}
            </Box>
          </Box>

          <IconButton
            aria-label="Scroll Right"
            icon={<ArrowForwardIcon />}
            onClick={scrollRight}
            variant="ghost"
            size="sm"
          />
        </Flex>
      )}
    </Box>
  );
};

export default CategoryBar;
